import React, { useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Flex,
  Badge,
  IconButton,
  Spinner,
  Center,
  useColorModeValue,
  useToast,
  Select,
  SimpleGrid,
  Avatar,
  Spacer
} from '@chakra-ui/react';
import { FiThumbsUp, FiThumbsDown, FiShare2, FiPlay } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import IconWrapper from '../components/IconWrapper';
import CachedImage from '../components/CachedImage';
import { useAuth } from '../contexts/AuthContext';
import { useSeries } from '../contexts/UnifiedContentContext';
import { GENRES } from '../constants/genres';
import AdSenseSlot from '../components/AdSenseSlot';
import { ADSENSE_CLIENT_ID, ADSENSE_SLOTS } from '../constants/adsense';

const Series: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const {
    series,
    loading,
    error,
    selectedGenre,
    userLikes,
    likeCounts,
    likingSeries,
    loadSeries,
    setSelectedGenre,
    handleLike,
    handleShare
  } = useSeries();

  // Color mode values
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'white');
  const titleColor = useColorModeValue('gray.900', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.400');

  const genres = [
    { id: 'all', name: 'All Genres' },
    ...GENRES.map(genre => ({ id: genre.toLowerCase(), name: genre }))
  ];

  useEffect(() => {
    loadSeries(user?.uid);
  }, [selectedGenre, loadSeries, user?.uid]);

  const onGenreChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedGenre(e.target.value);
  };

  if (loading) {
    return (
      <Center h="100vh">
        <Spinner size="xl" color="purple.500" />
      </Center>
    );
  }

  if (error || series.length === 0) {
    return (
      <Center h="50vh">
        <VStack gap={6} textAlign="center" maxW="400px">
          <Text fontSize="2xl" fontWeight="bold" color={titleColor}>
            Content Not Found
          </Text>
          <Text fontSize="md" color={labelColor}>
            Content not found or no longer available
          </Text>
          <VStack gap={3}>
            <Button onClick={() => loadSeries(user?.uid)} colorScheme="purple" size="lg">
              Refresh
            </Button>
            <Button 
              onClick={() => navigate('/upload')} 
              variant="outline" 
              colorScheme="purple"
            >
              Upload Content
            </Button>
          </VStack>
        </VStack>
      </Center>
    );
  }

  return (
    <Box bg={bgColor} w="100%" pt={0} mt={0} mb={0} pb={0}>
      <VStack gap={6} maxW="1400px" mx="auto" w="100%" pt={0} mt={0}>
        <AdSenseSlot
          client={ADSENSE_CLIENT_ID}
          slot={ADSENSE_SLOTS.CONTENT_LIST_BANNER}
          format="auto"
          fullWidthResponsive
          style={{ margin: '8px 0' }}
        />
        {/* Header */}
        <Flex justify="space-between" align="center" w="full">
          <Text fontSize="3xl" fontWeight="bold" color={titleColor}>
            Series
          </Text>
          <Select
            value={selectedGenre}
            onChange={onGenreChange}
            placeholder="Filter by Genre"
            maxW="200px"
            bg={cardBg}
            color={textColor}
            borderColor={labelColor}
          >
            {genres.map((genre) => (
              <option key={genre.id} value={genre.id}>
                {genre.name}
              </option>
            ))}
          </Select>
        </Flex>

        {/* Series Grid */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={6} w="full">
          {series.map((seriesItem) => (
            <Box
              key={seriesItem.id}
              bg={cardBg}
              borderRadius="lg"
              shadow="md"
              overflow="hidden"
              _hover={{ transform: 'translateY(-4px)', shadow: 'lg' }}
              transition="all 0.2s"
              cursor="pointer"
              onClick={() => navigate(`/content/${seriesItem.id}`)}
            >
              {/* Thumbnail */}
              <Box position="relative" h="200px">
                <CachedImage
                  src={seriesItem.poster || seriesItem.thumbnail}
                  alt={seriesItem.title}
                  w="100%"
                  h="100%"
                  objectFit="cover"
                />
                <Badge
                  position="absolute"
                  top={2}
                  left={2}
                  colorScheme="green"
                  fontSize="xs"
                  px={2}
                  py={1}
                  borderRadius="md"
                >
                  {seriesItem.category}
                </Badge>
                <IconButton
                  aria-label="Watch Series"
                  icon={<IconWrapper icon={FiPlay} size={24} />}
                  position="absolute"
                  bottom={2}
                  right={2}
                  colorScheme="green"
                  size="lg"
                  borderRadius="full"
                  onClick={() => navigate(`/content/${seriesItem.id}`)}
                />
              </Box>

              {/* Content Info */}
              <VStack p={4} align="flex-start" spacing={2}>
                <Text fontSize="lg" fontWeight="bold" color={textColor} noOfLines={1}>
                  {seriesItem.title}
                </Text>
                <HStack spacing={2} w="full" justify="space-between">
                  <HStack>
                    <Avatar size="xs" src={seriesItem.uploaderAvatar} name={seriesItem.uploader} />
                    <Text fontSize="sm" color={labelColor}>
                      {seriesItem.uploader}
                    </Text>
                  </HStack>
                  <Text fontSize="sm" color={labelColor}>
                    {seriesItem.views.toLocaleString()} views
                  </Text>
                </HStack>

                {/* Actions */}
                <HStack spacing={2} pt={2} w="full">
                  <Button
                    leftIcon={<IconWrapper icon={FiThumbsUp} size={16} />}
                    size="sm"
                    colorScheme={userLikes[seriesItem.id] === 'like' ? 'green' : 'gray'}
                    variant={userLikes[seriesItem.id] === 'like' ? 'solid' : 'outline'}
                    onClick={() => handleLike(seriesItem.id, user!.uid)}
                    isLoading={likingSeries.has(seriesItem.id)}
                    isDisabled={!user}
                  >
                    {likeCounts[seriesItem.id]?.likes || 0}
                  </Button>
                  <Button
                    leftIcon={<IconWrapper icon={FiThumbsDown} size={16} />}
                    size="sm"
                    colorScheme={userLikes[seriesItem.id] === 'dislike' ? 'red' : 'gray'}
                    variant={userLikes[seriesItem.id] === 'dislike' ? 'solid' : 'outline'}
                    onClick={() => handleLike(seriesItem.id, user!.uid)}
                    isLoading={likingSeries.has(seriesItem.id)}
                    isDisabled={!user}
                  >
                    {likeCounts[seriesItem.id]?.dislikes || 0}
                  </Button>
                  <Spacer />
                  <IconButton
                    aria-label="Share Series"
                    icon={<IconWrapper icon={FiShare2} size={16} />}
                    size="sm"
                    variant="outline"
                    colorScheme="purple"
                    onClick={() => handleShare(seriesItem.id, seriesItem.category, 'social', 'web', user?.uid)}
                    isDisabled={!user}
                  />
                </HStack>
              </VStack>
            </Box>
          ))}
        </SimpleGrid>
      </VStack>
    </Box>
  );
};

export default Series;
